import { createApi } from "@reduxjs/toolkit/query/react"
import axiosBaseQuery from "../../../axiosBaseQuery"
import { SELF_SERVICE_ENDPOINTS } from "../../../../api/constants"

/**
 * RTK Query API самообслуживания организации (`IsOrgAdmin` + скоуп своих организаций).
 *
 * Зачем это в ПОРТАЛЕ ХАБА, а не в админке клиники. Authority по перерасходу
 * принадлежит хабу: значение `overage_available` уезжает в подписанные entitlements
 * ответа `validate` и только оттуда попадает в `license.ModuleStatus` клиники.
 * Управляющего канала клиника→хаб не существует, поэтому переключатель живёт здесь
 * (ADR §7.8), а клиника показывает состояние read-only и ссылку сюда.
 *
 * Контракт лицензии (`GET self-service/licenses/`): {id, org_slug, org_name,
 * license_type, status, organization_name, branch_name, branch_city, valid_until,
 * last_synced_at, blocked_reason, modules[], created_at}; modules[] = {module_slug,
 * module_name, active, blocked, blocked_reason, request_limit, requests_used,
 * requests_remaining, period_start, period_end, period_months, overage_price,
 * overage_allowed_by_contract, overage_available}.
 *
 * `public_key` не отдаётся: витрине потребления ключ не нужен, а отдать его в браузер
 * значит размножить учётные данные инсталляции по рабочим местам сотрудников.
 *
 * Списки — UnifiedOffsetPagination: страница приезжает под ключом `results`.
 */
export const selfServiceApi = createApi({
  reducerPath: "selfServiceApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["SelfServiceLicense"],
  endpoints: builder => ({
    getMyLicenses: builder.query({
      query: ({ limit = 20, offset = 0, status, licenseType } = {}) => ({
        url: SELF_SERVICE_ENDPOINTS.LICENSES,
        method: "GET",
        params: {
          limit,
          offset,
          ...(status ? { status } : {}),
          ...(licenseType ? { license_type: licenseType } : {}),
        },
      }),
      providesTags: [{ type: "SelfServiceLicense", id: "LIST" }],
    }),

    /**
     * Переключатель перерасхода модуля.
     *
     * `overage_available` отправляется ВСЕГДА и явным булевым значением: у бэкенда
     * поле обязательное и без значения по умолчанию, потому что пустое тело нельзя
     * трактовать ни как «включить», ни как «выключить».
     *
     * Включение возможно только в границах договора: иначе 403
     * OVERAGE_NOT_ALLOWED_BY_CONTRACT. Дизейбл переключателя по
     * `overage_allowed_by_contract` — подсказка, а не гейт: бэкенд проверяет рамку
     * сам, внутри блокировки строки (рамку могли отозвать параллельно с кликом).
     */
    setModuleOverage: builder.mutation({
      query: ({ licenseId, moduleSlug, overageAvailable }) => ({
        url: SELF_SERVICE_ENDPOINTS.MODULE_OVERAGE(licenseId, moduleSlug),
        method: "POST",
        data: { overage_available: !!overageAvailable },
      }),
      invalidatesTags: [{ type: "SelfServiceLicense", id: "LIST" }],
    }),

    /**
     * Подтверждение единственности копии инсталляции.
     *
     * Зачем это в портале и почему у администратора организации. Хаб замечает вторую
     * работающую копию по отметке синхронизации, но отличить копию от восстановления из
     * резервной копии он не может — снаружи это одно и то же. Знает только тот, кто
     * восстанавливал, поэтому решение остаётся за администратором организации.
     *
     * После подтверждения привязка к серверу обнуляется: инсталляция запишет её заново при
     * следующей активации. Отзыв лицензии этим не снимается.
     */
    rebindLicense: builder.mutation({
      query: ({ licenseId }) => ({
        url: SELF_SERVICE_ENDPOINTS.REBIND(licenseId),
        method: "POST",
        data: {},
      }),
      invalidatesTags: [{ type: "SelfServiceLicense", id: "LIST" }],
    }),
  }),
})

export const {
  useGetMyLicensesQuery,
  useSetModuleOverageMutation,
  useRebindLicenseMutation,
} = selfServiceApi

export default selfServiceApi
